import React, { useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useQuery } from '@apollo/client/react';
import { Link, useParams } from 'react-router-dom';
import { Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faPrint } from '@fortawesome/free-solid-svg-icons';
import { Parser } from '@json2csv/plainjs';
import {
    GET_DETAILS_FOR_STAGE_SHEET, GET_STAGE_SHEET, StageSheetBox, StageTicket,
} from '../../../graphql/client/queries/stageQueries';
import { Loading, Error } from '../../_common';
import { Routes } from '../../../utils/Routes';
import { StageReadAccess } from './JobStageSummary';
import JobNavigation from '../JobNavigation';
import StageSheetPrintAllLayout from './StageSheetPrintAllLayout';
import './StageSummary.scss';

const weight = (value: number | null) => (value ?? 0).toLocaleString('en-US');

const TicketLink = ({ ticket, href }: { ticket: string | null; href: string | null }) => {
    const safeHref = href && /^https?:\/\//i.test(href) ? href : null;
    return safeHref ? <a href={safeHref} target="_blank" rel="noopener noreferrer">{ticket}</a> : <>{ticket}</>;
};

const LoadLink = ({ loadId }: { loadId: number | null }) => loadId
    ? <Link to={`${Routes.LOAD_PROFILE.route}${loadId}`}>{loadId}</Link> : null;

const SheetContent = ({ stageSheetId, history, bellyDump }: { stageSheetId: number; history: boolean; bellyDump: boolean }) => {
    const metadata = useQuery(GET_DETAILS_FOR_STAGE_SHEET, { variables: { stageSheetId }, fetchPolicy: 'network-only' });
    const sheet = useQuery(GET_STAGE_SHEET, { variables: { stageSheetId }, fetchPolicy: 'network-only' });
    const details = metadata.data?.getDetailsForStageSheet;
    const proppants = (sheet.data?.getStageSheet?.propDetails ?? []).filter(item => item != null);
    const boxes = (sheet.data?.getStageSheet?.details ?? []).filter((item): item is StageSheetBox => item != null);
    const isBellyDump = bellyDump;
    const title = 'Stage Sheet';
    const printAllRef = useRef<HTMLDivElement>(null);
    const handlePrintAll = useReactToPrint({
        contentRef: printAllRef,
        documentTitle: `${history ? 'stage-history' : 'stage-sheet'}-${stageSheetId}`,
        pageStyle: '@page { size: A4 landscape; margin: 10mm; }',
    });

    const download = () => {
        if (boxes.length === 0) return;
        const rows = boxes.map(box => {
            const record: Record<string, string | number> = {
                'Station#': box.station === '0' ? '' : box.station ?? '',
                Box: box.boxDesc ?? '',
                Weight: box.boxWeight ?? '',
                'Accumulated Weight': box.accumulatedWeight ?? '',
                Type: box.proppant ?? '',
                'Pad Location': box.location ?? '',
                'Wt. Remaining': box.boxWeightRemaining ?? '',
                OOS: box.isOOS ? 'Yes' : 'No',
                'PO #': `${box.sandPo ?? ''}\t`,
                'Origin #': `${box.lofaOrigin ?? ''}\t`,
            };
            const tickets = isBellyDump ? (box.stageSheetBellyDump ?? [])
                .filter((ticket): ticket is StageTicket => ticket != null) : [];
            if (tickets.length === 0) {
                record['Ticket #'] = `${box.sandTicket ?? ''}\t`;
                record['Load#'] = `${box.loadId ?? ''}\t`;
            } else {
                tickets.forEach((ticket, index) => {
                    record[`Load #${index + 1}`] = `${ticket.loadId ?? ''}\t`;
                    record[`Ticket #${index + 1}`] = `${ticket.sandTicket ?? ''}\t`;
                    record[`Weight #${index + 1}`] = `${ticket.sandTicketWeight ?? ''}\t`;
                    record[`Weight Remaining #${index + 1}`] = `${ticket.sandTicketWeightRemaining ?? ''}\t`;
                });
            }
            return record;
        });
        const url = URL.createObjectURL(new Blob([new Parser().parse(rows)], { type: 'text/csv;charset=utf-8;' }));
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'Stage Sheet.csv';
        document.body.appendChild(anchor);
        try {
            anchor.click();
        } finally {
            anchor.remove();
            setTimeout(() => URL.revokeObjectURL(url), 0);
        }
    };

    if (metadata.loading || sheet.loading) return <Loading />;
    if (metadata.error || sheet.error) return <Error error={(metadata.error ?? sheet.error)!} />;
    if (!details || !sheet.data?.getStageSheet) return <p role="status">Stage sheet not found.</p>;

    return <><div className="StageSheet-Page">
        <header className="StageSheet-Header-Container">
            <h2 className="StageSheet-Header">{title}: {details.wellName}</h2>
            <JobNavigation jobId={details.jobId} variant="stage" />
        </header>
        <div className="StageSheet-Subheader-Container">
            <Link className="StageSheet-Back-Button" to={Routes.STAGE_SUMMARY.route.replace(':jobId', String(details.jobId))}>Back</Link>
            <div className="StageSheet-Button-Container">
                <button type="button" className="StageSheet-Button" aria-label="Print stage sheet" onClick={handlePrintAll}>
                    <FontAwesomeIcon className="StageSheet-Icons" icon={faPrint} />Print All
                </button>
                <button type="button" className="StageSheet-Button" aria-label="Download stage sheet" disabled={boxes.length === 0} onClick={download}>
                    <FontAwesomeIcon className="StageSheet-Icons" icon={faDownload} />Download
                </button>
            </div>
        </div>
        <div className="StageSheet-Stages">{details.wellheadStages}</div>
        <section aria-label="Proppant summary" className="StageSheet-Grid-Container">
            <Table striped bordered className="StageSheet-Proppant-Details-Grid">
                <thead><tr><th>Proppant</th><th>Total Weight Needed</th><th>Sand Pumped</th><th>Cumulative Box Weight</th></tr></thead>
                <tbody>
                    {proppants.length === 0 && <tr><td colSpan={4}>No proppant details available.</td></tr>}
                    {proppants.map((prop, index) => <tr key={prop.stageSheetProppantId ?? index}>
                        <td>{prop.proppantName}</td><td className={(prop.totalWeightNeeded ?? 0) < 0 ? 'Td-Red-Background' : ''}>{weight(prop.totalWeightNeeded)}</td>
                        <td>{weight(prop.sandPumped)}</td><td>{weight(prop.totalBoxWeight)}</td>
                    </tr>)}
                </tbody>
            </Table>
        </section>
        <section aria-label={isBellyDump ? 'Belly dump box details' : 'Box details'} className="StageSheet-Grid-Container StageSheet-BoxGrid">
            <Table striped bordered className="StageSheet-Grid">
                <thead><tr>
                    <th>Station #</th><th>Box</th><th>{isBellyDump ? 'Box Wt.' : 'Weight'}</th>
                    {isBellyDump && <th>Box Wt. Remaining</th>}
                    <th>Type</th><th>Pad Location</th>
                    {!isBellyDump && <th>Wt. Remaining</th>}
                    <th>OOS</th><th>PO #</th><th>{isBellyDump ? 'Origin #' : 'Origin'}</th>
                    {isBellyDump ? <><th>Load#</th><th>Sand Ticket #</th><th>ST Weight</th><th>ST Wt. Remaining</th></>
                        : <><th>Ticket #</th><th>Load#</th></>}
                </tr></thead>
                <tbody>
                    {boxes.length === 0 && <tr><td colSpan={isBellyDump ? 13 : 11}>No box details available.</td></tr>}
                    {boxes.flatMap((box, boxIndex) => {
                        const tickets = isBellyDump
                            ? (box.stageSheetBellyDump ?? []).filter((ticket): ticket is StageTicket => ticket != null) : [];
                        const rows: (StageTicket | null)[] = tickets.length > 0 ? tickets : [null];
                        return rows.map((ticket, ticketIndex) => <tr key={`${box.stageSheetBoxId ?? boxIndex}-${ticket?.id ?? ticketIndex}`}>
                            {ticketIndex === 0 && <>
                                <td rowSpan={rows.length}>{box.station === '0' ? '' : box.station}</td>
                                <td rowSpan={rows.length}><div className="StageSheet-GreenDot-Container">{box.boxDesc}{box.isPartial === 'Yes' &&
                                    <span className="StageSheet-GreenDot" role="img" aria-label="Partial box" title="Partial box" />}</div></td>
                                <td rowSpan={rows.length}>{weight(box.boxWeight)}</td>
                                {isBellyDump && <td rowSpan={rows.length}>{weight(box.boxWeightRemaining)}</td>}
                                <td rowSpan={rows.length}>{box.proppant}</td><td rowSpan={rows.length}>{box.location}</td>
                                {!isBellyDump && <td rowSpan={rows.length}>{weight(box.boxWeightRemaining)}</td>}
                                <td rowSpan={rows.length}>{box.isOOS ? 'Yes' : 'No'}</td>
                                <td rowSpan={rows.length}>{box.sandPo}</td><td rowSpan={rows.length}>{box.lofaOrigin}</td>
                            </>}
                            {isBellyDump && <td><LoadLink loadId={ticket ? ticket.loadId : box.loadId} /></td>}
                            <td><TicketLink ticket={ticket ? ticket.sandTicket : box.sandTicket}
                                href={ticket ? ticket.sandTicketLink : box.sandTicketLink} /></td>
                            {isBellyDump && <>
                                <td>{ticket ? weight(ticket.sandTicketWeight) : ''}</td>
                                <td>{ticket ? weight(ticket.sandTicketWeightRemaining) : ''}</td>
                            </>}
                            {!isBellyDump && <td><LoadLink loadId={box.loadId} /></td>}
                        </tr>);
                    })}
                </tbody>
            </Table>
        </section>
    </div>
        <div className="StageSheet-PrintSource" aria-hidden="true">
            <StageSheetPrintAllLayout innerRef={printAllRef} jobName={details.wellName ?? ''}
                wellheadStages={details.wellheadStages ?? ''} proppantDetails={proppants} stageSheets={boxes} bellyDump={isBellyDump} />
        </div>
    </>;
};

const StageSheet = ({ history = false, bellyDump = false }: { history?: boolean; bellyDump?: boolean }) => {
    const { stageSheetId } = useParams();
    const id = Number(stageSheetId);
    return <StageReadAccess>{Number.isSafeInteger(id) && id > 0
        ? <SheetContent stageSheetId={id} history={history} bellyDump={bellyDump} />
        : <p role="alert">Invalid stage sheet.</p>}</StageReadAccess>;
};

export default StageSheet;